/**
 * Hover card of the vector QH 1/2000 layer (desktop pointers only). When the pointer rests ~150 ms
 * on a cell at zoom ≥ 15, a small card shows the category, ký hiệu, area, height, density and plan
 * status read from the vector tiles (no request) and the cell is outlined; it then follows the
 * pointer. Hidden while the map moves, while a tool captures clicks, off the map, or when the plan
 * in view is a raster. A click still opens the panel; touch screens keep tap → panel.
 */
import { esc, fmt, canHover } from './ui.js';
import { cardModel, cardPosition, CARD_MINZOOM } from './core/vplan.js';

const DWELL = 150;

/**
 * @param {import('./map.js').PlanMap} pm
 * @param {{ busy?: () => boolean, extra?: (lngLat: {lat:number,lng:number}) => string|null }} opts
 *   busy: true while a tool / dialog owns the pointer; extra: optional reference line (land price)
 */
export function initHoverCard(pm, { busy = () => false, extra = () => null } = {}) {
  const map = pm.map;
  const host = map.getContainer();
  const el = document.createElement('div');
  el.className = 'hovercard';
  el.setAttribute('role', 'tooltip');
  el.hidden = true;
  host.appendChild(el);

  let timer = 0, raf = 0, shown = null, last = null, moving = false;

  const eligible = () => canHover() && !moving && !busy() && !!pm.vectorPlan() && map.getZoom() >= CARD_MINZOOM;

  function hide() {
    clearTimeout(timer); timer = 0;
    if (raf) { cancelAnimationFrame(raf); raf = 0; }
    if (shown !== null) pm.hoverCell(null);
    shown = null;
    el.hidden = true;
  }

  function show(point) {
    if (!eligible()) { hide(); return; }
    const c = pm.cellAt(point);
    if (!c) { hide(); return; }
    const key = c.id ?? `${c.props.t}|${c.props.a}|${c.props.k}`;
    if (key !== shown) {
      const ll = map.unproject(point);
      el.innerHTML = render(c, extra({ lat: ll.lat, lng: ll.lng }));
      shown = key;
      pm.hoverCell(c.id);
    }
    el.hidden = false;
    const { x, y } = cardPosition(point.x, point.y, el.offsetWidth, el.offsetHeight, host.clientWidth, host.clientHeight);
    el.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
  }

  map.on('mousemove', (e) => {
    last = e.point;
    if (!eligible()) { if (shown !== null || timer) hide(); return; }
    if (shown !== null) {
      // visible: follow the pointer, update when it crosses into another cell
      raf ||= requestAnimationFrame(() => { raf = 0; if (last) show(last); });
      return;
    }
    clearTimeout(timer);
    timer = setTimeout(() => { timer = 0; if (last) show(last); }, DWELL);
  });
  map.on('mouseout', hide);
  map.on('mousedown', hide);
  map.on('movestart', () => { moving = true; hide(); });
  map.on('moveend', () => { moving = false; });
  addEventListener('keydown', (e) => { if (e.key === 'Escape') hide(); });
  return { hide };
}

function render(c, ref) {
  const m = cardModel(c.props, c.type, c.plan, { area: (a) => fmt.area(a) });
  return `<div class="hc-title"><span class="swatch" style="--sw:${esc(m.color)}"></span><b>${esc(m.title)}</b>${m.code ? `<span class="hc-code">${esc(m.code)}</span>` : ''}</div>`
    + (m.facts.length ? `<div class="hc-line">${m.facts.map(esc).join(' · ')}</div>` : '')
    + (m.ctx.length ? `<div class="hc-line hc-muted">${m.ctx.map(esc).join(' · ')}</div>` : '')
    + (ref ? `<div class="hc-line hc-muted">${esc(ref)}</div>` : '')
    + '<div class="hc-foot"><span>Bấm để xem chi tiết</span><span>Tham khảo</span></div>';
}
